import styled, { css, keyframes } from 'styled-components';
import { colors } from '../../../utils/utils';

export const ACCENT = '#5d5ff9';
const TEXT = '#1f1f1f';
const MUTED = '#777';
const BORDER = '#e5e5ea';
const DANGER = '#dc2626';

/**
 * One element for both states so the composer is never remounted (keeps focus and caret):
 * - collapsed: in-flow dock pinned to the bottom of the trainer column;
 * - expanded: covers the whole trainer column (header / messages / composer).
 * The positioned ancestor is the Trainer `Wrapper` (position: relative).
 */
export const Root = styled.section<{ $expanded: boolean }>`
    display: flex;
    flex-direction: column;
    align-self: stretch;
    flex: 0 0 auto;
    min-height: 0;
    background: ${colors.gray.wild_sand};
    border-top: 1px solid ${BORDER};

    ${({ $expanded }) =>
        $expanded &&
        css`
            position: absolute;
            inset: 0;
            z-index: 9;
            background: ${colors.white.basic};
            border-top: 0;
        `}
`;

export const Header = styled.header`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 4px;
    height: 40px;
    padding: 0 6px;
    border-bottom: 1px solid ${BORDER};
`;

export const Title = styled.div`
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    color: ${TEXT};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const IconButton = styled.button<{ $tone?: 'default' | 'primary' | 'danger' | 'success' }>`
    width: 28px;
    height: 28px;
    flex: 0 0 auto;
    padding: 0;
    border: 0;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: ${({ $tone }) => ($tone === 'primary' ? ACCENT : $tone === 'danger' ? DANGER : 'transparent')};
    color: ${({ $tone }) => {
        if ($tone === 'primary' || $tone === 'danger') return colors.white.basic;
        if ($tone === 'success') return '#16a34a';
        return MUTED;
    }};
    transition:
        background-color 0.15s,
        color 0.15s,
        opacity 0.15s;

    &:hover:not(:disabled) {
        background: ${({ $tone }) => ($tone === 'primary' || $tone === 'danger' ? undefined : '#f0f0f5')};
        color: ${({ $tone }) => ($tone === 'primary' || $tone === 'danger' || $tone === 'success' ? undefined : TEXT)};
    }

    &:focus-visible {
        outline: 2px solid ${ACCENT};
        outline-offset: 1px;
    }

    &:disabled {
        cursor: default;
        opacity: ${({ $tone }) => ($tone === 'primary' ? 0.3 : 0.4)};
    }
`;

/* ---------- messages ---------- */

export const MessagesViewport = styled.div`
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

export const MessagesScroller = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 12px 12px 4px 12px;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const EmptyState = styled.div`
    margin: auto 0;
    padding: 0 8px;
    text-align: center;
    color: ${MUTED};
    font-size: 13px;
    line-height: 1.45;
`;

export const UserMessage = styled.div`
    align-self: flex-end;
    max-width: 85%;
    padding: 7px 11px;
    border-radius: 14px 14px 4px 14px;
    background: #eef0ff;
    color: ${TEXT};
    font-size: 14px;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
`;

export const AssistantMessage = styled.div`
    align-self: stretch;
    color: ${TEXT};
`;

export const Markdown = styled.div`
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;

    p {
        margin: 0 0 8px 0;
    }

    p:last-child,
    ul:last-child,
    ol:last-child {
        margin-bottom: 0;
    }

    ul,
    ol {
        margin: 0 0 8px 0;
        padding-left: 18px;
    }

    li {
        margin: 0 0 4px 0;
    }

    code {
        padding: 1px 5px;
        border-radius: 4px;
        background: rgba(15, 23, 42, 0.07);
        font-size: 12px;
    }

    pre {
        margin: 0 0 8px 0;
        padding: 8px;
        border-radius: 8px;
        background: rgba(15, 23, 42, 0.05);
        overflow-x: auto;
    }

    pre code {
        padding: 0;
        background: none;
    }

    a {
        color: #1d4ed8;
    }
`;

export const MessageMeta = styled.div`
    margin-top: 6px;
    font-size: 11px;
    line-height: 1.4;
    color: #999;
`;

const blink = keyframes`
    0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
    40% { opacity: 1; transform: translateY(-2px); }
`;

export const TypingIndicator = styled.div`
    display: flex;
    gap: 4px;
    padding: 6px 0;

    span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #9a9aa5;
        animation: ${blink} 1.2s infinite ease-in-out;
    }

    span:nth-child(2) {
        animation-delay: 0.15s;
    }

    span:nth-child(3) {
        animation-delay: 0.3s;
    }
`;

export const JumpToLatest = styled(IconButton)`
    position: absolute;
    right: 12px;
    bottom: 8px;
    background: ${colors.white.basic};
    color: ${TEXT};
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.16);

    &:hover:not(:disabled) {
        background: ${colors.white.basic};
    }
`;

/* ---------- dock / composer ---------- */

export const Dock = styled.div`
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 10px;
`;

export const Notice = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 4px 4px 10px;
    border-radius: 8px;
    background: #fff1f0;
    border: 1px solid #ffccc7;
    color: #cf1322;
    font-size: 12px;
    line-height: 1.35;
`;

export const NoticeText = styled.div`
    flex: 1;
    min-width: 0;
    word-break: break-word;
`;

export const NoticeAction = styled.button`
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    padding: 2px 4px;
    font-size: 12px;
    font-weight: 700;
    color: #cf1322;
    cursor: pointer;
`;

export const ComposerBox = styled.div<{ $focused: boolean; $recording: boolean }>`
    display: flex;
    align-items: flex-end;
    gap: 4px;
    min-height: 40px;
    padding: 5px;
    border-radius: 20px;
    background: ${colors.white.basic};
    border: 1px solid ${({ $focused, $recording }) => ($recording ? '#fca5a5' : $focused ? ACCENT : '#d9d9d9')};
    box-shadow: ${({ $focused, $recording }) => ($recording ? '0 0 0 3px #fee2e2' : $focused ? '0 0 0 3px #5d5ff922' : 'none')};
    transition:
        border-color 0.15s,
        box-shadow 0.15s;
`;

export const TextArea = styled.textarea`
    flex: 1;
    min-width: 0;
    height: 28px;
    margin: 0;
    padding: 4px 4px 4px 6px;
    border: 0;
    outline: none;
    resize: none;
    overflow-y: hidden;
    background: transparent;
    color: ${TEXT};
    font: inherit;
    font-size: 14px;
    line-height: 20px;

    &::placeholder {
        color: #9a9aa5;
    }

    &:disabled {
        background: transparent;
        cursor: default;
    }
`;

const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.45); }
    70% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
    100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

export const RecorderStatus = styled.div`
    flex: 1;
    min-width: 0;
    height: 28px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 4px;
    font-size: 13px;
    color: ${TEXT};
`;

export const RecordingDot = styled.span`
    width: 8px;
    height: 8px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: ${DANGER};
    animation: ${pulse} 1.4s infinite;
`;

export const Spinner = styled.span`
    width: 12px;
    height: 12px;
    flex: 0 0 auto;
    border-radius: 50%;
    border: 2px solid #d9d9e3;
    border-top-color: ${ACCENT};
    animation: ${spin} 0.8s linear infinite;
`;

export const Timer = styled.span`
    margin-left: auto;
    color: ${DANGER};
    font-variant-numeric: tabular-nums;
`;
